import React, { useState } from 'react';

const ListingFilters = ({ filters, onFilterChange, onRefresh }) => {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const handleInputChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const handleReset = () => {
    onFilterChange({
      search: '',
      status: '',
      moderation_status: '',
      category_id: '',
      state_id: '',
      lga_id: '',
      lcda_id: '',
      is_boosted: '',
      is_featured: '',
      min_price: '',
      max_price: '',
      date_from: '',
      date_to: '',
      order_by: 'created_at',
      order_direction: 'DESC'
    });
  };

  const applyFilters = () => {
    onRefresh();
  };

  return (
    <div className="bg-gray-800 rounded-2xl shadow-lg border border-gray-700 p-6 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-white">Filter Listings</h3>
          <p className="text-gray-400 mt-1">Refine your search with filters</p>
        </div>
        <button
          onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
          className="px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all duration-200 hover:scale-[1.02] group"
          style={{ 
            color: '#ff660e',
            backgroundColor: '#ff660e15',
            border: '1px solid #ff660e30'
          }}
        >
          <span className={`transition-transform ${isAdvancedOpen ? 'rotate-180' : ''}`}>▼</span>
          <span>{isAdvancedOpen ? 'Hide Advanced' : 'Show Advanced'}</span>
        </button>
      </div>

      {/* Basic Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
            <span className="text-[#ff660e]">🔍</span>
            <span>Search</span>
          </label>
          <input
            type="text"
            placeholder="Title, description, store..."
            value={filters.search}
            onChange={(e) => handleInputChange('search', e.target.value)}
            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 bg-gray-700 text-white placeholder-gray-500"
            style={{ 
              borderColor: filters.search ? '#ff660e' : '#4b5563',
              '--tw-ring-color': '#ff660e80'
            }}
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
            <span className="text-[#ff660e]">📊</span>
            <span>Status</span>
          </label>
          <select
            value={filters.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 appearance-none bg-gray-700 text-white"
            style={{ 
              borderColor: filters.status ? '#ff660e' : '#4b5563',
              '--tw-ring-color': '#ff660e80'
            }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="sold">Sold</option>
          </select>
        </div>

        {/* Moderation Status */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
            <span className="text-[#ff660e]">⚖️</span>
            <span>Moderation</span>
          </label>
          <select
            value={filters.moderation_status}
            onChange={(e) => handleInputChange('moderation_status', e.target.value)}
            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 appearance-none bg-gray-700 text-white"
            style={{ 
              borderColor: filters.moderation_status ? '#ff660e' : '#4b5563',
              '--tw-ring-color': '#ff660e80'
            }}
          >
            <option value="">All Moderation</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="flagged">Flagged</option>
          </select>
        </div>

        {/* Boost Status */}
        <div>
          <label className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
            <span className="text-[#ff660e]">🚀</span>
            <span>Boost</span>
          </label>
          <select
            value={filters.is_boosted}
            onChange={(e) => handleInputChange('is_boosted', e.target.value)}
            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 appearance-none bg-gray-700 text-white"
            style={{ 
              borderColor: filters.is_boosted ? '#ff660e' : '#4b5563',
              '--tw-ring-color': '#ff660e80'
            }}
          >
            <option value="">All</option>
            <option value="true">Boosted</option>
            <option value="false">Not Boosted</option>
          </select>
        </div>
      </div>

      {/* Advanced Filters */}
      {isAdvancedOpen && (
        <div className="animate-fadeIn">
          <div className="border-t border-gray-700 pt-6 mb-6">
            <h4 className="text-lg font-semibold text-white mb-4">Advanced Filters</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Price Range */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Min Price (₦)</label>
                <input
                  type="number"
                  placeholder="0"
                  value={filters.min_price}
                  onChange={(e) => handleInputChange('min_price', e.target.value)}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 bg-gray-700 text-white placeholder-gray-500"
                  style={{ 
                    borderColor: filters.min_price ? '#ff660e' : '#4b5563',
                    '--tw-ring-color': '#ff660e80'
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Max Price (₦)</label>
                <input
                  type="number"
                  placeholder="1000000"
                  value={filters.max_price}
                  onChange={(e) => handleInputChange('max_price', e.target.value)}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 bg-gray-700 text-white placeholder-gray-500"
                  style={{ 
                    borderColor: filters.max_price ? '#ff660e' : '#4b5563',
                    '--tw-ring-color': '#ff660e80'
                  }}
                />
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">From Date</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleInputChange('date_from', e.target.value)}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 bg-gray-700 text-white"
                  style={{ 
                    borderColor: filters.date_from ? '#ff660e' : '#4b5563',
                    '--tw-ring-color': '#ff660e80'
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">To Date</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleInputChange('date_to', e.target.value)}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 bg-gray-700 text-white"
                  style={{ 
                    borderColor: filters.date_to ? '#ff660e' : '#4b5563',
                    '--tw-ring-color': '#ff660e80'
                  }}
                />
              </div>

              {/* Featured Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Featured</label>
                <select
                  value={filters.is_featured}
                  onChange={(e) => handleInputChange('is_featured', e.target.value)}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 appearance-none bg-gray-700 text-white"
                  style={{ 
                    borderColor: filters.is_featured ? '#ff660e' : '#4b5563',
                    '--tw-ring-color': '#ff660e80'
                  }}
                >
                  <option value="">All</option>
                  <option value="true">Featured Only</option>
                  <option value="false">Not Featured</option>
                </select>
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Sort By</label>
                <select
                  value={filters.order_by}
                  onChange={(e) => handleInputChange('order_by', e.target.value)}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 appearance-none bg-gray-700 text-white"
                  style={{ 
                    borderColor: filters.order_by !== 'created_at' ? '#ff660e' : '#4b5563',
                    '--tw-ring-color': '#ff660e80'
                  }}
                >
                  <option value="created_at">Date Created</option>
                  <option value="price">Price</option>
                  <option value="title">Title</option>
                  <option value="view_count">Views</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Sort Direction</label>
                <select
                  value={filters.order_direction}
                  onChange={(e) => handleInputChange('order_direction', e.target.value)}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-200 appearance-none bg-gray-700 text-white"
                  style={{ 
                    borderColor: filters.order_direction !== 'DESC' ? '#ff660e' : '#4b5563',
                    '--tw-ring-color': '#ff660e80'
                  }}
                >
                  <option value="DESC">Descending (Newest First)</option>
                  <option value="ASC">Ascending (Oldest First)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-700">
        <div className="text-sm text-gray-400">
          <p>Use filters to narrow down listings. Advanced filters provide more precise control.</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleReset}
            className="px-5 py-3 rounded-xl font-medium border-2 transition-all duration-200 hover:shadow hover:scale-[1.02] flex items-center gap-2 group"
            style={{ 
              borderColor: '#ff660e',
              color: '#ff660e',
              backgroundColor: 'transparent'
            }}
          >
            <span className="group-hover:rotate-180 transition-transform duration-500">🔄</span>
            <span>Reset All</span>
          </button>
          
          <button
            onClick={applyFilters}
            className="px-5 py-3 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg hover:scale-[1.02] flex items-center gap-2 bg-gradient-to-r from-[#ff660e] to-orange-500"
          >
            <span className="group-hover:scale-110 transition-transform">🔍</span>
            <span>Apply Filters</span>
          </button>
        </div>
      </div>

      {/* Active Filters Badges */}
      {(filters.search || filters.status || filters.moderation_status || 
        filters.min_price || filters.max_price || filters.is_boosted || filters.is_featured) && (
        <div className="mt-6 pt-6 border-t border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium text-white">Active Filters:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {filters.search && (
              <span className="px-3 py-1.5 bg-[#ff660e]/20 text-[#ff660e] text-sm font-medium rounded-full flex items-center gap-2 border border-[#ff660e]/30">
                <span>🔍</span>
                <span>{filters.search}</span>
                <button 
                  onClick={() => handleInputChange('search', '')}
                  className="text-[#ff660e] hover:text-orange-300 transition-colors"
                >
                  ×
                </button>
              </span>
            )}
            
            {filters.status && (
              <span className="px-3 py-1.5 bg-blue-900/30 text-blue-400 text-sm font-medium rounded-full flex items-center gap-2 border border-blue-800/50">
                <span>📊</span>
                <span>Status: {filters.status}</span>
                <button 
                  onClick={() => handleInputChange('status', '')}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  ×
                </button>
              </span>
            )}
            
            {filters.moderation_status && (
              <span className="px-3 py-1.5 bg-yellow-900/30 text-yellow-400 text-sm font-medium rounded-full flex items-center gap-2 border border-yellow-800/50">
                <span>⚖️</span>
                <span>Moderation: {filters.moderation_status}</span>
                <button 
                  onClick={() => handleInputChange('moderation_status', '')}
                  className="text-yellow-400 hover:text-yellow-300 transition-colors"
                >
                  ×
                </button>
              </span>
            )}
            
            {filters.is_boosted && (
              <span className="px-3 py-1.5 bg-purple-900/30 text-purple-400 text-sm font-medium rounded-full flex items-center gap-2 border border-purple-800/50">
                <span>🚀</span>
                <span>Boosted: {filters.is_boosted === 'true' ? 'Yes' : 'No'}</span>
                <button 
                  onClick={() => handleInputChange('is_boosted', '')}
                  className="text-purple-400 hover:text-purple-300 transition-colors"
                >
                  ×
                </button>
              </span>
            )}
            
            {filters.is_featured && (
              <span className="px-3 py-1.5 bg-pink-900/30 text-pink-400 text-sm font-medium rounded-full flex items-center gap-2 border border-pink-800/50">
                <span>⭐</span>
                <span>Featured: {filters.is_featured === 'true' ? 'Yes' : 'No'}</span>
                <button 
                  onClick={() => handleInputChange('is_featured', '')}
                  className="text-pink-400 hover:text-pink-300 transition-colors"
                >
                  ×
                </button>
              </span>
            )}
            
            {(filters.min_price || filters.max_price) && (
              <span className="px-3 py-1.5 bg-green-900/30 text-green-400 text-sm font-medium rounded-full flex items-center gap-2 border border-green-800/50">
                <span>💰</span>
                <span>
                  {filters.min_price && `Min: ₦${parseInt(filters.min_price).toLocaleString()}`}
                  {filters.min_price && filters.max_price && ' - '}
                  {filters.max_price && `Max: ₦${parseInt(filters.max_price).toLocaleString()}`}
                </span>
                <button 
                  onClick={() => {
                    handleInputChange('min_price', '');
                    handleInputChange('max_price', '');
                  }}
                  className="text-green-400 hover:text-green-300 transition-colors"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { 
            opacity: 0; 
            transform: translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        select {
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ff660e' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 0.5rem center;
          background-repeat: no-repeat;
          background-size: 1.5em 1.5em;
          padding-right: 2.5rem;
        }
      `}</style>
    </div>
  );
};

export default ListingFilters;